import Swiper from 'swiper';

const ACTIVE_CLASS = 'single-formation__nav-link--active';
const MOBILE_LINK_ACTIVE_CLASS = 'single-formation__mobile-menu-link--active';
const BODY_MENU_OPEN_CLASS = 'single-formation-menu-is-open';
const MOBILE_MENU_OPEN_CLASS = 'single-formation__mobile-menu--open';
const MOBILE_MENU_TRANSITION_MS = 350;
const ROOT_SELECTOR = '.single-formation';
const NAV_ROOT_SELECTOR = '.single-formation__nav';
const MAIN_SELECTOR = '.single-formation__main';
const LINK_SELECTOR = '[data-formation-nav-target]';
const IN_PAGE_ANCHOR_SELECTOR = 'a[href^="#"]:not([data-formation-nav-target])';
const OTHERS_SWIPER_SELECTOR = '.single-formation__others-swiper';
const MOBILE_MENU_SELECTOR = '[data-formation-mobile-menu]';
const MOBILE_MENU_LINK_SELECTOR =
  '.single-formation__mobile-menu-link[data-formation-nav-target]';
const MOBILE_MENU_TOGGLE_SELECTOR = '[data-formation-mobile-nav-toggle]';
const MOBILE_MENU_CLOSE_SELECTOR = '[data-formation-mobile-menu-close]';
const MOBILE_MENU_BACKDROP_SELECTOR = '[data-formation-mobile-menu-backdrop]';
const MOBILE_MENU_PANEL_SELECTOR = '.single-formation__mobile-menu-panel';
const MOBILE_NAV_LABEL_SELECTOR = '[data-formation-mobile-nav-label]';
const SCROLL_OFFSET = 140;

const othersSwiperOptions = {
  breakpoints: {
    640: {
      slidesPerView: 2,
      spaceBetween: 24,
    },
    1025: {
      slidesPerView: 3,
      spaceBetween: 32,
    },
  },
  direction: 'horizontal' as const,
  grabCursor: true,
  mousewheel: true,
  preventInteractionOnTransition: true,
  slidesPerView: 1,
  spaceBetween: 20,
};

class SingleFormation {
  private readonly linkByTarget = new Map<string, HTMLAnchorElement>();
  private readonly mobileLinkByTarget = new Map<string, HTMLButtonElement>();
  private readonly titleByTarget = new Map<string, string>();
  private sectionOrder: string[] = [];
  private scrollSpyTicking = false;
  private mobileMenu: HTMLElement | null = null;
  private mobileMenuToggle: HTMLButtonElement | null = null;
  private mobileNavLabel: HTMLElement | null = null;
  private mobileMenuClosing = false;

  constructor() {
    this.initOthersSwiper();
    this.collectNavTargets();
    this.initDesktopNav();
    this.initMobileBar();
    this.initScrollSpy();
    this.initInPageAnchors();
  }

  private initOthersSwiper(): void {
    const swiperEl = document.querySelector(OTHERS_SWIPER_SELECTOR);

    if (!swiperEl) {
      return;
    }

    new Swiper(OTHERS_SWIPER_SELECTOR, othersSwiperOptions);
  }

  private collectNavTargets(): void {
    const main = document.querySelector(MAIN_SELECTOR);
    const root = document.querySelector(ROOT_SELECTOR);

    if (!main || !root) {
      return;
    }

    const seen = new Set<string>();
    const nodes = root.querySelectorAll<HTMLElement>(LINK_SELECTOR);

    nodes.forEach((node) => {
      const targetId = node.getAttribute('data-formation-nav-target');
      if (!targetId) {
        return;
      }

      const section = document.getElementById(targetId);
      if (!section || !main.contains(section)) {
        return;
      }

      if (!seen.has(targetId)) {
        seen.add(targetId);
        this.sectionOrder.push(targetId);

        const title = node.textContent?.trim();
        if (title) {
          this.titleByTarget.set(targetId, title);
        }
      }

      if (
        node instanceof HTMLAnchorElement &&
        node.closest(NAV_ROOT_SELECTOR)
      ) {
        this.linkByTarget.set(targetId, node);
      }

      if (
        node instanceof HTMLButtonElement &&
        node.matches(MOBILE_MENU_LINK_SELECTOR)
      ) {
        this.mobileLinkByTarget.set(targetId, node);
      }
    });
  }

  private initDesktopNav(): void {
    this.linkByTarget.forEach((link, targetId) => {
      link.addEventListener('click', (event) =>
        this.onNavClick(event, targetId),
      );
    });
  }

  private initScrollSpy(): void {
    if (this.sectionOrder.length === 0) {
      return;
    }

    this.setActiveFromHash();
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    window.addEventListener('resize', () => this.updateActiveFromScroll());
    window.addEventListener('hashchange', () => this.setActiveFromHash());
  }

  private initMobileBar(): void {
    this.mobileMenu = document.querySelector(MOBILE_MENU_SELECTOR);
    this.mobileMenuToggle = document.querySelector(MOBILE_MENU_TOGGLE_SELECTOR);
    this.mobileNavLabel = document.querySelector(MOBILE_NAV_LABEL_SELECTOR);

    if (!this.mobileMenu && !this.mobileMenuToggle) {
      return;
    }

    this.mobileMenuToggle?.addEventListener('click', () => {
      if (this.isMobileMenuOpen()) {
        void this.closeMobileMenu();
      } else {
        this.openMobileMenu();
      }
    });

    this.mobileMenu
      ?.querySelector(MOBILE_MENU_BACKDROP_SELECTOR)
      ?.addEventListener('click', () => {
        void this.closeMobileMenu();
      });

    this.mobileMenu
      ?.querySelector(MOBILE_MENU_CLOSE_SELECTOR)
      ?.addEventListener('click', () => {
        void this.closeMobileMenu();
      });

    this.mobileMenu
      ?.querySelector(MOBILE_MENU_PANEL_SELECTOR)
      ?.addEventListener('click', (event) => {
        const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
          MOBILE_MENU_LINK_SELECTOR,
        );

        if (!button) {
          return;
        }

        const targetId = button.getAttribute('data-formation-nav-target');
        if (!targetId) {
          return;
        }

        event.preventDefault();
        this.navigateToSection(targetId);
      });

    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape' &&
        this.mobileMenu &&
        !this.mobileMenu.hasAttribute('hidden')
      ) {
        void this.closeMobileMenu();
      }
    });

    window
      .matchMedia('(min-width: 1025px)')
      .addEventListener('change', (event) => {
        if (event.matches) {
          void this.closeMobileMenu();
        }
      });
  }

  private openMobileMenu(): void {
    if (!this.mobileMenu || !this.mobileMenuToggle) {
      return;
    }

    this.mobileMenuClosing = false;
    this.mobileMenu.removeAttribute('hidden');
    this.mobileMenuToggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add(BODY_MENU_OPEN_CLASS);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.mobileMenu?.classList.add(MOBILE_MENU_OPEN_CLASS);
      });
    });
  }

  private closeMobileMenu(): Promise<void> {
    if (!this.mobileMenu || !this.mobileMenuToggle) {
      return Promise.resolve();
    }

    if (
      this.mobileMenu.hasAttribute('hidden') ||
      !this.mobileMenu.classList.contains(MOBILE_MENU_OPEN_CLASS)
    ) {
      return Promise.resolve();
    }

    if (this.mobileMenuClosing) {
      return Promise.resolve();
    }

    this.mobileMenuClosing = true;
    this.mobileMenu.classList.remove(MOBILE_MENU_OPEN_CLASS);
    this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove(BODY_MENU_OPEN_CLASS);

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (prefersReducedMotion) {
      this.finishMobileMenuClose();
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const panel = this.mobileMenu?.querySelector<HTMLElement>(
        MOBILE_MENU_PANEL_SELECTOR,
      );
      let settled = false;

      const finish = (): void => {
        if (settled) {
          return;
        }
        settled = true;
        panel?.removeEventListener('transitionend', onTransitionEnd);
        this.finishMobileMenuClose();
        resolve();
      };

      const onTransitionEnd = (event: TransitionEvent): void => {
        if (event.target !== panel || event.propertyName !== 'transform') {
          return;
        }
        finish();
      };

      panel?.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(finish, MOBILE_MENU_TRANSITION_MS + 50);
    });
  }

  private finishMobileMenuClose(): void {
    this.mobileMenu?.setAttribute('hidden', '');
    this.mobileMenuClosing = false;
  }

  private isMobileMenuOpen(): boolean {
    return Boolean(
      this.mobileMenu?.classList.contains(MOBILE_MENU_OPEN_CLASS),
    );
  }

  /** Ferme le menu mobile puis scroll vers la section (ordre requis sur mobile). */
  private navigateToSection(targetId: string): void {
    const menuWasOpen = this.isMobileMenuOpen();

    const scroll = (): void => {
      this.scrollToTarget(targetId);

      if (this.titleByTarget.has(targetId)) {
        this.setActiveLink(targetId);
      }
    };

    if (menuWasOpen) {
      void this.closeMobileMenu().then(scroll);
      return;
    }

    scroll();
  }

  /** Décodage du fragment d’ancre ; retourne null si la séquence % est invalide. */
  private decodeAnchorFragment(fragment: string): string | null {
    try {
      return decodeURIComponent(fragment);
    } catch {
      return null;
    }
  }

  private initInPageAnchors(): void {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) {
      return;
    }

    const links = Array.from(
      root.querySelectorAll<HTMLAnchorElement>(IN_PAGE_ANCHOR_SELECTOR),
    );

    links.forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href === '#') {
        return;
      }

      const targetId = this.decodeAnchorFragment(href.slice(1));
      if (!targetId || !document.getElementById(targetId)) {
        return;
      }

      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.navigateToSection(targetId);
      });
    });
  }

  private onScroll(): void {
    if (this.scrollSpyTicking) {
      return;
    }

    this.scrollSpyTicking = true;
    requestAnimationFrame(() => {
      this.updateActiveFromScroll();
      this.scrollSpyTicking = false;
    });
  }

  /**
   * Section visible au centre du viewport ; sinon celle dont le centre vertical
   * est le plus proche du milieu de l’écran.
   */
  private updateActiveFromScroll(): void {
    if (this.sectionOrder.length === 0) {
      return;
    }

    const viewportCenterY = window.innerHeight / 2;
    let activeId = this.sectionOrder[0];
    let containingCenterId: string | null = null;
    let closestDistance = Infinity;

    for (const id of this.sectionOrder) {
      const section = document.getElementById(id);
      if (!section) {
        continue;
      }

      const { top, bottom, height } = section.getBoundingClientRect();

      if (top <= viewportCenterY && bottom >= viewportCenterY) {
        containingCenterId = id;
        break;
      }

      const sectionCenterY = top + height / 2;
      const distance = Math.abs(sectionCenterY - viewportCenterY);
      if (distance < closestDistance) {
        closestDistance = distance;
        activeId = id;
      }
    }

    this.setActiveLink(containingCenterId ?? activeId);
  }

  private onNavClick(event: MouseEvent, targetId: string): void {
    this.onAnchorClick(event, targetId);
    this.setActiveLink(targetId);
  }

  private onAnchorClick(event: MouseEvent, targetId: string): void {
    event.preventDefault();
    this.scrollToTarget(targetId);
  }

  private scrollToTarget(targetId: string): void {
    const section = document.getElementById(targetId);
    if (!section) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';

    const top =
      section.getBoundingClientRect().top +
      window.scrollY -
      SCROLL_OFFSET;

    window.scrollTo({ top, behavior });

    history.pushState(null, '', `#${targetId}`);
  }

  private setActiveFromHash(): void {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash || !this.titleByTarget.has(hash)) {
      this.updateActiveFromScroll();
      return;
    }
    this.setActiveLink(hash);
  }

  private setActiveLink(targetId: string): void {
    this.linkByTarget.forEach((link, id) => {
      const isActive = id === targetId;
      link.classList.toggle(ACTIVE_CLASS, isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    this.mobileLinkByTarget.forEach((button, id) => {
      const isActive = id === targetId;
      button.classList.toggle(MOBILE_LINK_ACTIVE_CLASS, isActive);
      if (isActive) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });

    const title = this.titleByTarget.get(targetId);
    if (title && this.mobileNavLabel) {
      this.mobileNavLabel.textContent = title;
    }
  }
}

export const singleFormation = new SingleFormation();
